import * as busyCounter from '../../shared/action-creator/busy-counter';
import React, { Component } from 'react';
import { createErrorMessage, createSuccessMessage } from '../../shared/action-creator/alert-messages';
import { getAvailabilityUnits, getFamilyAvailability, updateFamilyAvailability } from './repositories';
import { ConfirmationModal } from '../../shared/components/modals/confirmation-modal';
import { Link } from 'react-router-dom';
import LabeledReactSelect from '../../shared/components/options-drop-down/labeled-react-select';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

const PAGE_SIZE = 25;
const initialState = () => ( {
    units: [], unitsLoading: true, unitsError: false, selectedUnit: '',
    rows: [], loading: false, saving: false, loadError: false,
    selected: [], scope: 'selected', target: 'yes',
    page: 1, descending: false, confirmation: null
} );

// Match the searchable Unit control on Batch Admin.
const selectStyles = {
    control: base => ( { ...base, minHeight: '28px', height: '28px' } ),
    valueContainer: base => ( { ...base, height: '28px', padding: '0 8px' } ),
    input: base => ( { ...base, margin: '0', padding: '0' } ),
    indicatorsContainer: base => ( { ...base, height: '28px' } ),
    option: base => ( { ...base, fontSize: '10px', padding: '2px 8px', minHeight: 'unset' } ),
    singleValue: base => ( { ...base, fontSize: '11px' } ),
    placeholder: base => ( { ...base, fontSize: '11px' } )
};
const filterUnitOption = ( option, input ) => option.value.toLowerCase().includes( input.trim().toLowerCase() );

export class FamilyAvailability extends Component {

    state = initialState();

    componentDidMount () {

        this.alive = true;
        this.loadUnits();

    }

    componentWillUnmount () {

        this.alive = false;
        this.cancelRequest();

    }

    loadUnits = async () => {

        const token = this.beginRequest( 'units' );
        if ( !token ) return;
        this.setState( { unitsLoading: true, unitsError: false } );
        let result;
        try {

            const units = await getAvailabilityUnits( token.controller.signal );
            if ( !Array.isArray( units ) || units.some( unit => !unit || typeof unit.name !== 'string' || !unit.name.trim() ) ) throw new Error( 'Invalid response' );
            result = { units, selectedUnit: units.some( unit => unit.name === this.props.unitCode ) ? this.props.unitCode : '' };

        } catch ( error ) {

            if ( this.isCurrent( token ) ) {

                this.setState( { units: [], selectedUnit: '', unitsError: true } );
                this.props.createErrorMessage( 'Could not load available units. Retry to retrieve your authorized units.' );

            }

        } finally {

            const current = this.isCurrent( token );
            this.finishRequest( token );
            if ( current ) this.setState( { unitsLoading: false, ...result }, result ? this.load : undefined );

        }

    };

    validUnit = () => this.state.units.some( unit => unit.name === this.state.selectedUnit );

    changeUnit = unitCode => {

        // A POST may already have committed: never retarget a write in flight.
        if ( this.state.unitsLoading || this.state.saving || ( this.request && this.request.kind === 'save' ) ) return;
        if ( unitCode && !this.state.units.some( unit => unit.name === unitCode ) ) return;
        this.cancelRequest();
        this.setState( { ...initialState(), units: this.state.units, unitsLoading: false, selectedUnit: unitCode }, this.load );

    };

    cancelRequest = () => {

        if ( this.request ) {

            this.request.controller.abort();
            this.finishRequest( this.request );
            this.request = null;

        }

    };

    beginRequest = ( kind = 'families' ) => {

        if ( this.request || !this.alive ) return null;
        const token = { controller: new AbortController(), unitCode: this.state.selectedUnit, kind, finished: false };
        this.request = token;
        this.props.increaseCounter();
        return token;

    };

    isCurrent = token => this.alive && this.request === token && token.unitCode === this.state.selectedUnit;

    finishRequest = token => {

        if ( !token.finished ) {

            Object.assign( token, { finished: true } );
            this.props.decreaseCounter();

        }
        if ( this.request === token ) this.request = null;

    };

    load = async () => {

        if ( !this.validUnit() || this.state.unitsLoading ) return;
        const token = this.beginRequest();
        if ( !token ) return;
        this.setState( { loading: true, loadError: false, selected: [], confirmation: null } );
        try {

            const rows = await getFamilyAvailability( token.unitCode, token.controller.signal );
            if ( !Array.isArray( rows ) ) throw new Error( 'Invalid response' );
            if ( this.isCurrent( token ) ) this.setState( previous => ( {
                rows, page: 1, selected: previous.scope === 'all' ? rows.map( row => row.family ) : []
            } ) );

        } catch ( error ) {

            if ( this.isCurrent( token ) ) {

                this.setState( { rows: [], loadError: true } );
                this.props.createErrorMessage( 'Could not load family availability. Retry or refresh to retrieve the current values.' );

            }

        } finally {

            if ( this.isCurrent( token ) ) this.setState( { loading: false } );
            this.finishRequest( token );

        }

    };

    sortedRows = () => [...this.state.rows].sort( ( left, right ) => ( this.state.descending ? -1 : 1 ) * left.family.localeCompare( right.family ) );

    targets = () => {

        if ( this.state.scope === 'all' ) return this.state.rows;
        return this.sortedRows().filter( row => this.state.selected.includes( row.family ) );

    };

    confirmRows = ( rows, systemNotAvailable, scope ) => {

        if ( this.request || !this.validUnit() || this.state.loadError || this.state.confirmation || !rows.length ) return;
        this.setState( { confirmation: {
            unitCode: this.state.selectedUnit,
            scope,
            systemNotAvailable,
            updates: rows.map( row => ( { family: row.family, expectedSystemNotAvail: row.system_not_avail } ) )
        } } );

    };

    closeConfirmation = () => {

        if ( !this.request ) this.setState( { confirmation: null } );

    };

    save = async () => {

        const snapshot = this.state.confirmation;
        if ( !snapshot || !this.validUnit() || snapshot.unitCode !== this.state.selectedUnit ) return;
        const token = this.beginRequest( 'save' ); // Synchronous lock guards double confirm clicks.
        if ( !token ) return;
        this.setState( { saving: true, confirmation: null } );
        let reload = false;
        try {

            const response = await updateFamilyAvailability( token.unitCode, snapshot, token.controller.signal );
            if ( this.isCurrent( token ) ) {

                this.props.createSuccessMessage( `Updated ${response.updatedCount} families for unit ${token.unitCode}.` );
                reload = true;

            }

        } catch ( error ) {

            if ( this.isCurrent( token ) ) {

                const messages = {
                    400: 'The update was invalid. Refresh and check the selected families.',
                    403: 'ATP setup administrator access is required to change family availability.',
                    409: 'Family availability changed since it was loaded. Current values will be reloaded; review and confirm again.'
                };
                const rejected = error.status === 400 || error.status === 403;
                const conflict = error.status === 409;
                this.props.createErrorMessage( messages[error.status] || 'The update outcome is unknown. Reloading the current unit to verify its availability.' );
                reload = conflict || !rejected;
                if ( reload && !conflict ) this.setState( { rows: [], selected: [], page: 1 } );

            }

        } finally {

            const current = this.isCurrent( token );
            if ( current ) this.setState( { saving: false, selected: [] } );
            this.finishRequest( token );
            // A failed reload is a load error, never a failed committed update.
            if ( current && reload ) this.load();

        }

    };

    changeScope = scope => this.setState( previous => ( {
        scope, selected: scope === 'all' ? previous.rows.map( row => row.family ) : previous.selected
    } ) );

    selectRow = ( family, checked ) => this.setState( previous => ( {
        scope: 'selected',
        selected: checked ? [...previous.selected, family] : previous.selected.filter( item => item !== family )
    } ) );

    selectVisible = ( visible, checked ) => this.setState( previous => ( {
        scope: 'selected',
        selected: [...previous.selected.filter( family => !visible.some( row => row.family === family ) ),
            ...( checked ? visible.map( row => row.family ) : [] )]
    } ) );

    render () {

        const { units, unitsLoading, unitsError, selectedUnit, rows, loading, saving, loadError, selected, scope, target, page, confirmation } = this.state;
        const visible = this.sortedRows().slice( ( page - 1 ) * PAGE_SIZE, page * PAGE_SIZE );
        const unavailable = rows.filter( row => row.system_not_available ).length;
        const disabled = unitsLoading || !this.validUnit() || loading || saving || !!confirmation;
        const count = this.targets().length;
        const pages = Math.max( 1, Math.ceil( rows.length / PAGE_SIZE ) );
        const unitOptions = [...units].sort( ( left, right ) => left.name.localeCompare( right.name ) )
            .map( unit => ( { value: unit.name, label: unit.description ? `${unit.name} - ${unit.description}` : unit.name } ) );

        return (
            <main className="family-availability" aria-busy={unitsLoading || loading || saving}>
                <header className="family-availability__header">
                    <h2 className="page-header">Reset SNA / Family availability</h2>
                    <Link className="family-availability-link" to="/batch-admin">Batch Admin</Link>
                </header>
                <p className="family-availability__description">Manage access to ATP by family for the selected unit.</p>
                <div className="family-availability__overview">
                    <fieldset disabled={unitsLoading || loading || saving} className="family-availability__units">
                        <legend className="sr-only">Select authorized unit</legend>
                        <LabeledReactSelect
                            className="family-availability__unit-select"
                            label="Unit"
                            inputId="availability-unit"
                            isPlaceholderTranslated={false}
                            isLabelTranslated={false}
                            onChange={option => this.changeUnit( option ? option.value : '' )}
                            optionKey="value"
                            optionDisplayAttributes={['label']}
                            options={unitOptions}
                            placeholder="Select a unit"
                            selectedValue={unitOptions.find( option => option.value === selectedUnit ) || null}
                            disabled={unitsLoading || loading || saving}
                            styles={selectStyles}
                            isSearchable={true}
                            isClearable={true}
                            filterOption={filterUnitOption}
                        />
                        <button type="button" className="secondary-button cancel-button family-availability__refresh" disabled={disabled} onClick={this.load}>Refresh</button>
                    </fieldset>
                    {this.validUnit() && (
                        <div className="family-availability__summary" aria-label="Unit family totals">
                            <div><span>Total families</span><strong>{rows.length}</strong></div>
                            <div className="available"><span>Available · No</span><strong>{rows.length - unavailable}</strong></div>
                            <div className="unavailable"><span>Unavailable · Yes</span><strong>{unavailable}</strong></div>
                        </div>
                    )}
                </div>
                {unitsLoading && <p role="status">Loading available units…</p>}
                {unitsError && <div role="alert" className="alert alert-danger">Available units could not be loaded. <button type="button" className="btn btn-outline-danger" disabled={unitsLoading} onClick={this.loadUnits}>Retry available units</button></div>}
                {!unitsLoading && !unitsError && !units.length && <p>No authorized units are available.</p>}
                {!unitsLoading && !!units.length && !selectedUnit && <p>Select a unit to load family availability.</p>}
                {this.validUnit() && <p className="family-availability__help">System not available: <strong>Yes disables access</strong>; No makes the family available. Changes require ATP setup administrator access.</p>}
                {this.validUnit() && (
                    <fieldset disabled={disabled || !rows.length} className="family-availability__bulk">
                        <legend>Bulk update</legend>
                        <label htmlFor="availability-scope">Target scope
                            <select id="availability-scope" className="form-control" value={scope} onChange={event => this.changeScope( event.target.value )}>
                                <option value="selected">Selected families</option><option value="all">All families in this unit</option>
                            </select>
                        </label>
                        <label htmlFor="availability-target">Set system not available
                            <select id="availability-target" className="form-control" value={target} onChange={event => this.setState( { target: event.target.value } )}>
                                <option value="yes">Yes — System not available for access</option><option value="no">No — System available for access</option>
                            </select>
                        </label>
                        <button type="button" className="btn btn-primary" disabled={disabled || !count} onClick={() => this.confirmRows( this.targets(), target === 'yes', scope )}>Review update ({count})</button>
                    </fieldset>
                )}
                {this.validUnit() && <p className="family-availability__count" role="status">{rows.length} families · {selected.length} selected</p>}
                {loading && <p role="status">Loading family availability…</p>}
                {loadError && <div role="alert" className="alert alert-danger">Family availability could not be loaded. <button type="button" className="btn btn-outline-danger" disabled={disabled} onClick={this.load}>Retry</button></div>}
                {this.validUnit() && !loading && !loadError && !rows.length && <p>No families exist for this unit.</p>}
                {!!rows.length && (
                    <div className="family-availability__table" role="region" aria-label="Family availability table">
                        <table className="table table-hover">
                            <caption className="sr-only">Family availability for unit {selectedUnit}</caption>
                            <thead>
                                <tr>
                                    <th scope="col" aria-sort={this.state.descending ? 'descending' : 'ascending'}>
                                        <div className="family-availability__family-cell">
                                            <input type="checkbox" aria-label="Select all visible families" disabled={disabled || !visible.length} checked={!!visible.length && visible.every( row => selected.includes( row.family ) )} onChange={event => this.selectVisible( visible, event.target.checked )} />
                                            <button type="button" className="btn btn-link" disabled={disabled} onClick={() => this.setState( { descending: !this.state.descending, page: 1 } )}>Family {this.state.descending ? '↓' : '↑'}</button>
                                        </div>
                                    </th>
                                    <th scope="col">System not available</th>
                                </tr>
                            </thead>
                            <tbody>
                                {visible.map( row => (
                                    <tr key={row.family} className={selected.includes( row.family ) ? 'family-availability__row--selected' : undefined}>
                                        <th scope="row">
                                            <div className="family-availability__family-cell">
                                                <input type="checkbox" aria-label={`Select family ${row.family}`} disabled={disabled} checked={selected.includes( row.family )} onChange={event => this.selectRow( row.family, event.target.checked )} />
                                                <span className="family-availability__family-name">{row.family}</span>
                                            </div>
                                        </th>
                                        <td>
                                            <label className={`family-availability__switch ${row.system_not_available ? 'unavailable' : 'available'}`}>
                                                <input type="checkbox" role="switch" aria-checked={row.system_not_available} aria-label={`System not available for family ${row.family}`} checked={row.system_not_available} disabled={disabled} onChange={event => this.confirmRows( [row], event.target.checked, `family ${row.family}` )} />
                                                <span>{row.system_not_available ? 'Yes — Unavailable' : 'No — Available'}</span>
                                            </label>
                                        </td>
                                    </tr>
                                ) )}
                            </tbody>
                        </table>
                    </div>
                )}
                {pages > 1 && (
                    <nav className="family-availability__pagination" aria-label="Family table pagination">
                        <button type="button" className="btn btn-outline-secondary" disabled={disabled || page === 1} onClick={() => this.setState( { page: page - 1 } )}>Previous</button>
                        <span>Page {page} of {pages}</span>
                        <button type="button" className="btn btn-outline-secondary" disabled={disabled || page === pages} onClick={() => this.setState( { page: page + 1 } )}>Next</button>
                    </nav>
                )}
                <ConfirmationModal
                    isOpen={!!confirmation}
                    header="Confirm family availability change"
                    onCancel={this.closeConfirmation}
                    onHide={this.closeConfirmation}
                    onConfirm={this.save}
                    body={confirmation ? <div><p>Setting SNA flag: <strong>{confirmation.systemNotAvailable ? 'Yes' : 'No'} - {confirmation.unitCode}</strong> and selected families: {confirmation.updates.length}</p><p>Are you sure you want to continue?</p></div> : <span />}
                />
            </main>
        );

    }

}

FamilyAvailability.propTypes = {
    unitCode: PropTypes.string,
    increaseCounter: PropTypes.func.isRequired,
    decreaseCounter: PropTypes.func.isRequired,
    createErrorMessage: PropTypes.func.isRequired,
    createSuccessMessage: PropTypes.func.isRequired
};

export default connect( state => ( { unitCode: state.application.unit && state.application.unit.name } ), {
    increaseCounter: busyCounter.increaseBusyCounter,
    decreaseCounter: busyCounter.decreaseBusyCounter,
    createErrorMessage,
    createSuccessMessage
} )( FamilyAvailability );